import {
  leadsTypesCreateMutation,
  leadsTypesDestroyMutation,
  leadsTypesListOptions,
  leadsTypesListQueryKey,
  leadsTypesPartialUpdateMutation,
  leadsTypesRetrieveOptions,
  PaginatedReturnLeadTypeListReadable,
  ReturnLeadTypeReadable
} from '@crm/api'
import { ConfirmModal, DndTable } from '@crm/components'
import {
  FormModal,
  FormNomenclaturesLeadTypes
} from '@crm/features/nomenclatures/components'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { useTableSorter } from '@crm/hooks/useTableSorter'
import { paths } from '@crm/routes'
import { defaultFilters } from '@crm/utils'
import { ConditionalText, DayLabel, TableActions } from '@shared/components'
import {
  useInvalidatePredicateQueries,
  useQueryParams,
  useResetPagination
} from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { getFullName, showApiErrors } from '@shared/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App, Button, Form, MenuProps, TableColumnsType } from 'antd'
import { Add, Edit, Status, Trash } from 'iconsax-react'

import { useCallback, useEffect, useMemo, useState } from 'react'

const formName = 'lead-types'

export const NomenclaturesLeadTypes = () => {
  const { t } = useLocales()
  const params = useQueryParams()
  const sorter = useTableSorter()

  const [editItem, setEditItem] = useState<number>()
  const [deleteItem, setDeleteItem] = useState<number>()
  const [isCreate, setIsCreate] = useState(false)
  const navigate = useAppNavigate()

  const { notification } = App.useApp()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { resetPagination } = useResetPagination()
  const queryClient = useQueryClient()

  const [sorted, setSorted] = useState(false)
  const [data, setData] = useState<PaginatedReturnLeadTypeListReadable>()

  const [form] = Form.useForm()
  const [showResponsibleWarning, setShowResponsibleWarning] = useState(false)

  const onToggle = () => setIsCreate((prevState) => !prevState)

  const { data: leadsTypes, isLoading } = useQuery(
    leadsTypesListOptions({ query: { ...defaultFilters, ...params } })
  )

  const createMutation = useMutation({
    ...leadsTypesCreateMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([leadsTypesListQueryKey()])
      queryClient.refetchQueries()

      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    }
  })

  const updateMutation = useMutation({
    ...leadsTypesPartialUpdateMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([leadsTypesListQueryKey()])
      queryClient.refetchQueries()

      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    }
  })

  useEffect(() => {
    if (!leadsTypes) return
    setData(leadsTypes)
  }, [leadsTypes])

  const getActionColumnItem: (id: number) => MenuProps['items'] = useCallback(
    (id) => [
      {
        label: t('nomenclature.edit'),
        key: 'edit',
        icon: <Edit size={20} />,
        onClick: () => setEditItem(id)
      },
      {
        label: t('nomenclature.edit_statuses'),
        icon: <Status size={20} />,
        key: 'edit_statuses',
        onClick: () =>
          navigate(paths.admin.nomenclatures.leads.type(String(id)))
      },
      { type: 'divider' },
      {
        label: t('nomenclature.delete'),
        icon: <Trash size={20} />,
        danger: true,
        key: 'delete',
        onClick: () => setDeleteItem(id)
      }
    ],
    [navigate, t]
  )

  const columns: TableColumnsType<ReturnLeadTypeReadable> = useMemo(
    () => [
      {
        title: t('nomenclature.name'),
        dataIndex: 'name',
        key: 'name',
        sorter: true
      },
      {
        title: t('nomenclature.code'),
        dataIndex: 'code',
        key: 'code',
        sorter: true
      },
      {
        title: t('nomenclature.order'),
        dataIndex: 'order',
        key: 'order',
        sorter: true
      },
      {
        title: t('nomenclature.responsible'),
        dataIndex: 'responsible',
        key: 'responsible',
        sorter: true,
        render: (responsible) => (
          <ConditionalText condition={!!responsible}>
            {getFullName(responsible)}
          </ConditionalText>
        )
      },
      {
        title: t('nomenclature.is_default'),
        key: 'is_default',
        sorter: true,
        render: ({ is_default }) =>
          is_default ? t('buttons.yes') : t('buttons.no')
      },
      {
        title: t('filter.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        render: (created_at) => <DayLabel date={created_at} />
      },
      {
        title: t('lead.modified_at'),
        dataIndex: 'modified_at',
        key: 'modified_at',
        sorter: true,
        render: (modified_at) => <DayLabel date={modified_at} />
      },
      {
        width: 50,
        key: 'actions',
        fixed: 'right',
        render: ({ id }) => <TableActions options={getActionColumnItem(id)} />
      }
    ],
    [t, getActionColumnItem]
  )

  useEffect(() => {
    if (data?.results && sorted) {
      setData(
        (prevState) =>
          ({
            ...prevState,
            results: data?.results?.map(({ id, ...params }, i) => {
              if (
                i + 1 !==
                leadsTypes?.results?.find((item) => item.id === id)?.order
              ) {
                updateMutation.mutate({
                  path: { id },
                  body: {
                    ...params,
                    responsible: params.responsible?.id,
                    order: i + 1
                  }
                })

                return { id, ...params, order: i + 1 }
              }

              return { id, ...params }
            })
          } as PaginatedReturnLeadTypeListReadable)
      )
      setSorted(false)
    }
  }, [data, leadsTypes?.results, sorted, updateMutation])

  const deleteLeadTypeMutation = useMutation({
    ...leadsTypesDestroyMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([leadsTypesListQueryKey()])
      resetPagination()
      onCloseModal()
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_delete')
      })
    }
  })

  const dndItems = data?.results?.map(({ id }) => id) || []

  const onCloseModal = () => {
    setDeleteItem(undefined)
    setEditItem(undefined)
    setIsCreate(false)
  }

  const onRow = (data: ReturnLeadTypeReadable) => ({
    onClick: () =>
      navigate(paths.admin.nomenclatures.leads.type(String(data.id)))
  })

  const onDelete = () => {
    if (deleteItem) deleteLeadTypeMutation.mutate({ path: { id: deleteItem } })
  }

  const onSortEnd = (
    action: (state: ReturnLeadTypeReadable[]) => ReturnLeadTypeReadable[]
  ) => {
    setData((prev) => {
      const results = action(prev?.results || [])

      return {
        ...prev,
        count: prev?.count || 0,
        results: results || prev?.results
      }
    })

    setSorted(true)
  }

  const onSubmit = async (force = false) => {
    const values = await form.validateFields()

    if (!values.responsible && !force) {
      setShowResponsibleWarning(true)
      return
    }

    if (!values.responsible && force) {
      values.responsible = null
    }

    if (editItem) {
      updateMutation.mutate({ path: { id: editItem }, body: values })
    } else {
      createMutation.mutate({ body: values })
    }

    queryClient.refetchQueries()
    setShowResponsibleWarning(false)
    onCloseModal()
  }

  return (
    <>
      <DndTable
        onDragEnd={onSortEnd}
        rightChildren={
          <Button type="primary" onClick={onToggle} icon={<Add size={20} />}>
            {t('nomenclature.add_type')}
          </Button>
        }
        items={dndItems}
        data={data}
        columns={columns}
        loading={isLoading}
        onChange={(_, __, result) => sorter(result)}
        onRow={onRow}
        rowKey="id"
        rowClassName="pointer"
        bordered
      />

      <FormModal
        form={form}
        id={editItem}
        formName={formName}
        open={!!(editItem || isCreate)}
        getItem={leadsTypesRetrieveOptions}
        onCreate={leadsTypesCreateMutation}
        onUpdate={leadsTypesPartialUpdateMutation}
        onCloseModal={onCloseModal}
        onFinish={onSubmit}
      >
        <FormNomenclaturesLeadTypes form={form} />
      </FormModal>

      <ConfirmModal
        open={showResponsibleWarning}
        header={t('warning')}
        okText={t('buttons.continue_anyway')}
        cancelText={t('buttons.cancel')}
        onSuccess={() => onSubmit(true)}
        onCancel={() => setShowResponsibleWarning(false)}
      >
        {t('pipeline_no_responsible_warning')}
      </ConfirmModal>

      <ConfirmModal
        open={!!deleteItem}
        onSuccess={onDelete}
        onCancel={onCloseModal}
        confirmLoading={deleteLeadTypeMutation.isPending}
      />
    </>
  )
}
